import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const BankAccountSchema = z.object({
  id: z.string(),
  label: z.string(),
  accountNumber: z.string(),
  accountHolder: z.string(),
  bankName: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  currency: z.string(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const CreateBankAccountSchema = z.object({
  label: z.string().min(1),
  accountNumber: z.string().min(1),
  accountHolder: z.string().min(1),
  bankName: z.string().optional(),
  iban: z.string().optional(),
  bic: z.string().optional(),
  currency: z.string().min(3),
  notes: z.string().optional(),
});

const UpdateBankAccountSchema = CreateBankAccountSchema.partial().extend({
  isActive: z.boolean().optional(),
});

const errorSchema = z.object({ message: z.string() });

export const bankAccountContract = c.router({
  listBankAccounts: {
    method: "GET",
    path: "/billing/bank-accounts",
    query: z.object({
      includeInactive: z.coerce.boolean().optional().default(false),
    }),
    responses: {
      200: z.array(BankAccountSchema),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "List bank accounts",
  },

  getBankAccount: {
    method: "GET",
    path: "/billing/bank-accounts/:id",
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: BankAccountSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get a bank account by id",
  },

  createBankAccount: {
    method: "POST",
    path: "/billing/bank-accounts",
    body: CreateBankAccountSchema,
    responses: {
      201: BankAccountSchema,
      400: errorSchema,
      401: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Create a bank account",
  },

  updateBankAccount: {
    method: "PATCH",
    path: "/billing/bank-accounts/:id",
    pathParams: z.object({ id: z.string() }),
    body: UpdateBankAccountSchema,
    responses: {
      200: BankAccountSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Update a bank account",
  },

  deactivateBankAccount: {
    method: "DELETE",
    path: "/billing/bank-accounts/:id",
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: BankAccountSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Deactivate a bank account (soft delete)",
  },
});

export type BankAccountContract = typeof bankAccountContract;
